import {
  Table,
  OverlayTrigger,
  Tooltip,
  Modal,
  Row,
  Button,
} from "react-bootstrap";

import { useEffect, useRef, useState } from "react";
import {
  addStandardChargesLineItems,
  deleteStandardChargesLineItems,
  getStandardChargesByCaseID,
  getStandardChargesList,
  updateStandardChargesLineItems,
} from "../../../../services/ManageInvoiceAPI";
import Snackbar from "../../../../common/Snackbar";
import {
  formatAppointmentDateTime,
  formatDateTime,
  formattedDate,
} from "../../../../utils/dateUtils";
import SearchableSingleSelectDropdown from "../../../../common/SearchableDropdown";
import TableSingleSelectDropdown from "../../../../common/TableSingleSelectDropdown";
import InvoiceCommentsDrawer from "./InvoiceCommentsDrawer";
import ViewComments from "./ViewComments";
import NormalDatePicker from "../../../../common/NormalDatePicker";
import { INVOICE_STATUS } from "../../../../common/Constant";

// Define the DataTableProps interface
interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];

  caseInfoID?: any;
  searchQuery?: any;
  onAddSuccess?: () => void;
  invoiceStatus: string;
}

const ChargesEditTableData: React.FC<DataTableProps> = ({
  headers,
  caseInfoID,
  searchQuery,
  onAddSuccess,
  invoiceStatus,
}) => {
  const [chargesData, setChargesData] = useState<any[]>([]);
  const [chargesOption, setChargesOption] = useState<any[]>([]);
  const [showAddRow, setShowAddRow] = useState(false);

  const [selectedChargeId, setSelectedChargeId] = useState<any>(null);

  const [quantity, setQuantity] = useState<any>("");
  const [amount, setAmount] = useState<any>("");
  const [currenecySymbol, setCurencySymbol] = useState<any>("");
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");

  const [openComments, setOpenModal] = useState(false);
  const [effectiveDateFrom, setEffectiveDateFrom] = useState<Date | null>(
    new Date(),
  );

  const getStandardChargesOption = async () => {
    const list = await getStandardChargesList(caseInfoID);
    return list;
  };

  const getStandardChargesData = async () => {
    const searchPayload = {
      caseInfoId: caseInfoID,
      searchText: searchQuery,
    };

    const response = await getStandardChargesByCaseID(searchPayload);

    const mappedData = response.map((item: any) => ({
      ...item,
      fixedFlatRate: `${item.currencySymbol} ${Number(item.fixedFlatRate ?? 0).toFixed(2)}`,
      generatedBy: `${formatAppointmentDateTime(item.effectiveChargeDate)} | ${item.createdByName}`,
    }));

    setChargesData(mappedData);
  };

  useEffect(() => {
    if (!caseInfoID) return;

    const loadData = async () => {
      try {
        const options = await getStandardChargesOption();
        setChargesOption(options);

        // ensure options exist before mapping
        await getStandardChargesData();
      } catch (error) {
        console.error("Error loading standard charges:", error);
      }
    };

    loadData();
  }, [caseInfoID, searchQuery]);

  const handleChargeSelect = (charge: any) => {
    if (!charge) return;

    setSelectedChargeId(charge.value);
    setAmount(charge.chargesRate);

    setCurencySymbol(charge.currencySymbol);
  };

  const handleCreateStandardCharges = () => {
    resetEditRow();
    setShowAddRow(true);
  };

  const validateCharges = () => {
    const errors = [];

    if (!selectedChargeId) {
      errors.push("Please select a charge.");
    }

    if (!quantity) {
      errors.push("Please enter the quantity / number of pages.");
    }

    if (!amount) {
      errors.push("Please enter the amount.");
    }

    if (errors?.length > 0) {
      setSnackbarMessage(errors.join("\n"));
      setSnackbarOpen(true);
      setError("error");
      return false;
    }

    return true;
  };

  const onSaveStandardCharges = async () => {
    if (!validateCharges()) return;
    try {
      const addPayload = {
        caseInfoId: caseInfoID,
        billingConfigStdChgId: selectedChargeId,
        totalQuantityPgLn: Number(quantity),
        fixedFlatRate: Number(amount),
        comments: "",
        effectiveChargeDate: effectiveDateFrom
          ? formatDateTime(effectiveDateFrom)
          : null,
      };

      const response = await addStandardChargesLineItems(addPayload);

      if (response?.isSuccess) {
        resetAddRow();
        setSnackbarMessage("Charges have been added successfully");
        setError("success");
        setSnackbarOpen(true);
        await getStandardChargesData();

        onAddSuccess?.();
      }
    } catch (error) {
      console.error("Error adding standard charges:", error);
    }
  };

  const handleEditRow = (row: any) => {
    resetAddRow();
    setEditingRowId(row.invoiceLineItemStdChgId);

    setSelectedChargeId(row.billingConfigStdChgId);
    setQuantity(String(row.totalQuantityPgLn ?? ""));
    setCurencySymbol(row.currencySymbol);

    const cleanAmount =
      typeof row.fixedFlatRate === "string"
        ? row.fixedFlatRate.replace(/[^0-9.]/g, "")
        : String(row.fixedFlatRate ?? "");

    setAmount(cleanAmount);
    setEffectiveDateFrom(row.effectiveChargeDate);
  };

  const onUpdateStandardCharges = async (row: any) => {
    if (!validateCharges()) return;
    try {
      const editPayload = {
        invoiceLineItemStdChgId: editingRowId,
        billingConfigStdChgId: selectedChargeId,
        totalQuantityPgLn: Number(quantity),
        fixedFlatRate: Number(amount),
        comments: "",
        effectiveChargeDate: effectiveDateFrom
          ? formatDateTime(effectiveDateFrom)
          : null,
      };

      const response = await updateStandardChargesLineItems(editPayload);

      if (response?.isSuccess) {
        resetEditRow();
        setSnackbarMessage("Charges have been updated successfully");
        setError("success");
        setSnackbarOpen(true);
        await getStandardChargesData();

        onAddSuccess?.();
      }
    } catch (error) {
      console.error("Error adding standard charges:", error);
    }
  };

  const handleDeleteRow = (row: any) => {
    setDeletingRowId(row.invoiceLineItemStdChgId);
    resetEditRow();
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteRow = async () => {
    try {
      const response = await deleteStandardChargesLineItems(deletingRowId);

      if (response?.isSuccess) {
        setSnackbarMessage("Charges have been deleted successfully");
        setError("success");
        setSnackbarOpen(true);
        setDeletingRowId(null);
        setShowDeleteModal(false);
        await getStandardChargesData();

        onAddSuccess?.();
      }
    } catch (error) {
      console.error("Error adding standard charges:", error);
    }
  };

  const handleCloseDeleteModal = () => {
    setEditingRowId(null);
    setShowDeleteModal(false);
  };

  const resetEditRow = () => {
    setEditingRowId(null);
    setSelectedChargeId(null);
    setQuantity("");
    setAmount("");
    setEffectiveDateFrom(new Date());
  };

  const resetAddRow = () => {
    setShowAddRow(false);
    setSelectedChargeId(null);
    setQuantity("");
    setAmount("");
    setEffectiveDateFrom(new Date());
  };

  useEffect(() => {
    if (showAddRow && tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [showAddRow]);

  const openCommentsModal = (row: any) => {
    setOpenModal(true);
    setEditingRowId(row.invoiceLineItemStdChgId);
  };

  const closeCommentsModal = () => {
    setOpenModal(false);
    setEditingRowId(null);
  };

  const disabledAction =
    invoiceStatus == INVOICE_STATUS.INVOICED ||
    invoiceStatus == INVOICE_STATUS.CANCELLED ||
    invoiceStatus == INVOICE_STATUS.GENERATED;

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />
      <div className="border-0 mb-2 sticky-table-wrapper acutransGrida">
        <div
          className="table-responsive border-0 mb-2 adjustInvoiceGri"
          ref={tableContainerRef}
        >
          <Table>
            <thead>
              <tr>
                {headers.map((header) => (
                  // <th key={header.key} style={thStyle}>
                  <th
                    key={header.key}
                    style={{
                      border: "none",
                      padding: "8px",
                      backgroundColor: "#e0f7fa",
                      color: "#000",
                      cursor: header.sortable ? "pointer" : "default",
                      position: header.key === "actions" ? "sticky" : "sticky",
                      top: 0,
                      right: header.key === "actions" ? 0 : undefined,
                      zIndex: header.key === "actions" ? 5 : -90,

                      backgroundClip: "padding-box",

                      textAlign: ["actions", "totalQuantityPgLn"].includes(
                        header.key,
                      )
                        ? "center"
                        : "left",
                      ...header.style,
                    }}
                  >
                    <div
                      style={{
                        display: ["actions", "totalQuantityPgLn"].includes(
                          header.key,
                        )
                          ? "flex"
                          : "flex",

                        justifyContent: [
                          "actions",
                          "totalQuantityPgLn",
                        ].includes(header.key)
                          ? "space-evenly"
                          : "space-between",

                        alignItems: "center",
                      }}
                    >
                      <span>{header.label}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chargesData?.length > 0 &&
                chargesData.map((row, index) => (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: index % 2 === 1 ? "white" : "#f2f2f2",
                    }}
                  >
                    {headers.map((header) => {
                      const cellContent = row[header.key];

                      const isEditDisabled =
                        row.invoiceLineItemStatus === "Invoice Generated";

                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: header.isNumeric
                              ? "8px 20px 8px 8px"
                              : "8px",
                            textAlign: header.isNumeric ? "right" : "left",
                          }}
                        >
                          {header.key === "actions" ? (
                            editingRowId === row.invoiceLineItemStdChgId ? (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "left",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: "pointer",
                                    color: "#0092d5",
                                  }}
                                  onClick={() => onUpdateStandardCharges(row)}
                                >
                                  save
                                </span>

                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: "pointer",
                                    color: "#dc3545",
                                  }}
                                  onClick={resetEditRow}
                                >
                                  close
                                </span>
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: "pointer",
                                    color: "#0092d5",
                                  }}
                                  onClick={() => openCommentsModal(row)}
                                >
                                  forum
                                </span>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "left",
                                  gap: "8px",
                                }}
                              >
                                {/* Edit */}
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: isEditDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                    color: isEditDisabled ? "#ccc" : "#0092d5",
                                    opacity: isEditDisabled ? 0.6 : 1,
                                  }}
                                  onClick={
                                    !isEditDisabled
                                      ? () => handleEditRow(row)
                                      : undefined
                                  }
                                >
                                  edit
                                </span>

                                {/* Delete */}
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: disabledAction
                                      ? "not-allowed"
                                      : "pointer",
                                    color: disabledAction ? "#ccc" : "#dc3545",
                                    opacity: disabledAction ? 0.6 : 1,
                                  }}
                                  onClick={
                                    !disabledAction
                                      ? () => handleDeleteRow(row)
                                      : undefined
                                  }
                                >
                                  delete
                                </span>

                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    cursor: disabledAction
                                      ? "not-allowed"
                                      : "pointer",
                                    color: disabledAction ? "#ccc" : "#0092d5",
                                    opacity: disabledAction ? 0.6 : 1,
                                  }}
                                  onClick={
                                    !disabledAction
                                      ? () => openCommentsModal(row)
                                      : undefined
                                  }
                                >
                                  forum
                                </span>
                              </div>
                            )
                          ) : editingRowId === row.invoiceLineItemStdChgId ? (
                            header.key === "standardChargeNote" ? (
                              <TableSingleSelectDropdown
                                value={selectedChargeId}
                                options={chargesOption}
                                placeholder="Search charge..."
                                onChange={(charge) =>
                                  handleChargeSelect(charge)
                                }
                              />
                            ) : header.key === "totalQuantityPgLn" ? (
                              <input
                                type="text"
                                className="form-control form-control-sm text-end"
                                value={quantity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) setQuantity(value);
                                }}
                              />
                            ) : header.key === "generatedBy" ? (
                              <NormalDatePicker
                                value={effectiveDateFrom}
                                onChange={(date) => setEffectiveDateFrom(date)}
                                maxDate={new Date()}
                              />
                            ) : header.key === "fixedFlatRate" ? (
                              <div className="input-group input-group-sm">
                                <input
                                  type="text"
                                  className="form-control form-control-sm text-end"
                                  value={amount}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const regex = /^\d{0,11}(\.\d{0,2})?$/;

                                    if (value === "" || regex.test(value)) {
                                      setAmount(value);
                                    }
                                  }}
                                />
                                <span className="input-group-text">
                                  {currenecySymbol}
                                </span>
                              </div>
                            ) : (
                              cellContent
                            )
                          ) : (
                            cellContent
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}

              {showAddRow && (
                <tr style={{ backgroundColor: "#fffef1" }}>
                  {/* Charge Name */}

                  <td>
                    <TableSingleSelectDropdown
                      value={selectedChargeId}
                      options={chargesOption}
                      placeholder="Search charge..."
                      onChange={(charge) => handleChargeSelect(charge)}
                    />
                  </td>

                  {/* Qty / Pages */}
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          setQuantity(value);
                        }
                      }}
                    />
                  </td>

                  {/* Invoice Status */}
                  <td>Ready To Bill</td>

                  {/* Generated On | By */}
                  <td>
                    <NormalDatePicker
                      value={effectiveDateFrom}
                      onChange={(date) => setEffectiveDateFrom(date)}
                      maxDate={new Date()}
                    />
                  </td>

                  {/* Amount */}
                  <td>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control text-end"
                        value={amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          const regex = /^\d{0,11}(\.\d{0,2})?$/;

                          if (value === "" || regex.test(value)) {
                            setAmount(value);
                          }
                        }}
                      />
                      <span className="input-group-text">
                        {currenecySymbol}
                      </span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td style={{ textAlign: "center" }}>
                    <span
                      className="material-symbols-outlined me-2"
                      style={{ cursor: "pointer", color: "#0092d5" }}
                      onClick={onSaveStandardCharges}
                    >
                      save
                    </span>

                    <span
                      className="material-symbols-outlined"
                      style={{ cursor: "pointer", color: "#dc3545" }}
                      onClick={resetAddRow}
                    >
                      close
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              {!showAddRow && (
                <tr>
                  <td
                    colSpan={headers.length}
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      backgroundColor: "#f4f7fe",
                      position: "sticky",
                      bottom: "-2px",
                      zIndex: 20,
                    }}
                  >
                    <button
                      onClick={handleCreateStandardCharges}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0092d5",
                        cursor: "pointer",
                      }}
                      disabled={disabledAction}
                    >
                      <span className="material-icons me-2">add</span>
                      Create Standard Charges
                    </button>
                  </td>
                </tr>
              )}
            </tfoot>
          </Table>
          <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
            <Modal.Body className="fw-bold text-left modelheading ">
              <div>Are you sure you want to delete this charges?</div>
            </Modal.Body>
            <Modal.Footer className="border-top d-flex justify-content-end">
              <Button
                variant="outline-info"
                className="d-flex align-items-end"
                onClick={handleCloseDeleteModal}
              >
                <span className="material-icons mr-2">close</span> Cancel
              </Button>
              <Button
                onClick={handleConfirmDeleteRow}
                variant="primary"
                className="ms-3 d-flex align-items-center"
              >
                <span className="material-icons mx-1">check</span> Confirm
              </Button>
            </Modal.Footer>
          </Modal>
          {openComments && (
            <InvoiceCommentsDrawer
              title="View Comments"
              open={openComments}
              onClose={closeCommentsModal}
              placement="end"
              width={"70%"}
              height={"auto"}
              top={"105px"}
            >
              <ViewComments
                lineItemID={editingRowId}
                onClose={closeCommentsModal}
              />
            </InvoiceCommentsDrawer>
          )}
        </div>
      </div>
    </>
  );
};

export default ChargesEditTableData;
